<template>
  <div class="spm-filter-card">
    <!-- Card Header -->
    <div class="spm-filter-card__header">
      <div class="spm-filter-card__field-info">
        <i class="fa-regular fa-filter spm-filter-card__icon" />
        <span class="spm-filter-card__field-name">{{ label }}</span>
      </div>
      <Button
        class="p-button-text p-button-rounded p-button-sm spm-filter-card__remove"
        icon="fa-regular fa-times"
        @click="$emit('remove')"
      />
    </div>

    <!-- Card Body -->
    <div class="spm-filter-card__body">
      <!-- Operator selector (for text and number types) -->
      <Dropdown
        v-if="showOperatorSelector"
        v-model="selectedOperator"
        :options="operatorOptions"
        option-label="label"
        option-value="value"
        class="spm-filter-card__operator"
        :placeholder="t('spmTableApi.filters.placeholders.singleSelect')"
      />

      <!-- Single Select (dropdown) -->
      <Dropdown
        v-if="filterType === 'singleSelect'"
        v-model="filterValue"
        :options="resolvedOptions"
        option-label="label"
        option-value="value"
        :placeholder="t('spmTableApi.filters.placeholders.singleSelect')"
        :show-clear="true"
        class="spm-filter-card__input"
      />

      <!-- Multi Select -->
      <MultiSelect
        v-else-if="filterType === 'multiSelect'"
        v-model="filterValue"
        :options="resolvedOptions"
        option-label="label"
        option-value="value"
        :placeholder="t('spmTableApi.filters.placeholders.multiSelect')"
        :filter="true"
        :show-clear="true"
        class="spm-filter-card__input"
      />

      <!-- Language Select -->
      <LanguageMultiDropdown
        v-else-if="filterType === 'languageSelect'"
        v-model="filterValue"
        :options="resolvedOptions"
        :placeholder="t('spmTableApi.filters.placeholders.languageSelect')"
        :show-label="false"
        class="spm-filter-card__input"
      />

      <!-- Country Select -->
      <CountryMultiDropdown
        v-else-if="filterType === 'countrySelect'"
        v-model="filterValue"
        :placeholder="t('spmTableApi.filters.placeholders.countrySelect')"
        :show-label="false"
        class="spm-filter-card__input"
      />

      <!-- Date Range -->
      <SpmDateRangePicker
        v-else-if="filterType === 'dateRange'"
        v-model="filterValue"
        :min-years-back="5"
        class="spm-filter-card__input"
      />

      <!-- Number Input -->
      <template v-else-if="filterType === 'numberInput'">
        <InputNumber
          v-if="!isRangeOperatorSelected"
          v-model="filterValue"
          :placeholder="t('spmTableApi.filters.placeholders.numberInput')"
          class="spm-filter-card__input"
          :use-grouping="false"
        />
        <div
          v-else
          class="spm-filter-card__range"
        >
          <InputNumber
            v-model="rangeStart"
            :placeholder="t('spmTableApi.filters.placeholders.numberInput')"
            class="spm-filter-card__range-input"
            :use-grouping="false"
          />
          <span class="spm-filter-card__range-separator">→</span>
          <InputNumber
            v-model="rangeEnd"
            :placeholder="t('spmTableApi.filters.placeholders.numberInput')"
            class="spm-filter-card__range-input"
            :use-grouping="false"
          />
        </div>
      </template>

      <!-- Text Input (default) -->
      <InputText
        v-else
        v-model="filterValue"
        :placeholder="t('spmTableApi.filters.placeholders.textInput')"
        class="spm-filter-card__input"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  computed,
  ref,
  watch,
  onMounted,
  PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import MultiSelect from 'primevue/multiselect';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Button from 'primevue/button';
import LanguageMultiDropdown from '@/components/fields/LanguageMultiDropdown.vue';
import CountryMultiDropdown from '@/components/fields/CountryMultiDropdown.vue';
import SpmDateRangePicker from '@/components/SpmUI/SpmDateRangePicker.vue';
import type { SpmFieldConfig } from '../../types';
import type { SpmActiveFilter, SpmFilterOption } from '../types';
import {
  SpmFilterOperator,
  SpmFilterType,
  isRangeOperator,
} from '../operators';
import {
  getFilterType,
  getAvailableOperators,
  getDefaultOperator,
  isDateRangeFilterValue,
} from '../utils';

export default defineComponent({
  name: 'SpmFilterInput',

  components: {
    Dropdown,
    MultiSelect,
    InputText,
    InputNumber,
    Button,
    LanguageMultiDropdown,
    CountryMultiDropdown,
    SpmDateRangePicker,
  },

  props: {
    fieldConfig: {
      type: Object as PropType<SpmFieldConfig>,
      required: true,
    },

    modelValue: {
      type: Object as PropType<SpmActiveFilter | null>,
      default: null,
    },

    label: {
      type: String,
      default: '',
    },
  },

  emits: ['update:modelValue', 'remove'],

  setup(props, { emit }) {
    const { t } = useI18n();

    // Get filter configuration
    const filterType = computed<SpmFilterType>(() => getFilterType(props.fieldConfig.filterConfig));

    // Available operators for this filter type
    const availableOperators = computed(() => getAvailableOperators(props.fieldConfig.filterConfig));

    // Show operator selector for text and number inputs
    const showOperatorSelector = computed(() => filterType.value === 'textInput' || filterType.value === 'numberInput');

    // Operator options for dropdown
    const operatorOptions = computed(() => availableOperators.value.map((op) => ({
      value: op,
      label: t(`spmTableApi.filters.operators.${op}`),
    })));

    // Selected operator
    const selectedOperator = ref<SpmFilterOperator>(
      props.modelValue?.operator ?? getDefaultOperator(props.fieldConfig.filterConfig),
    );

    // Filter value
    const filterValue = ref<unknown>(props.modelValue?.values ?? null);

    // Range values for BETWEEN operators
    const rangeStart = ref<number | null>(null);
    const rangeEnd = ref<number | null>(null);

    // Check if current operator is a range operator
    const isRangeOperatorSelected = computed(() => isRangeOperator(selectedOperator.value));

    // Resolved options (handle async options)
    const resolvedOptions = ref<SpmFilterOption[]>([]);

    // Load options on mount
    onMounted(async () => {
      const options = props.fieldConfig.filterConfig?.options;
      if (options) {
        if (typeof options === 'function') {
          resolvedOptions.value = await options();
        } else {
          resolvedOptions.value = options;
        }
      }
    });

    // Initialize values from modelValue (handles dateRange, range operators, and regular values)
    watch(
      () => props.modelValue,
      (newVal) => {
        if (newVal) {
          selectedOperator.value = newVal.operator;

          // Handle dateRange filter type - values is a DateRangeFilterValue object
          if (filterType.value === 'dateRange' && isDateRangeFilterValue(newVal.values)) {
            filterValue.value = newVal.values;
            return;
          }

          // Handle range operators (BETWEEN) for number inputs
          if (isRangeOperator(newVal.operator) && Array.isArray(newVal.values)) {
            rangeStart.value = newVal.values[0] as number ?? null;
            rangeEnd.value = newVal.values[1] as number ?? null;
          } else {
            filterValue.value = newVal.values;
          }
        }
      },
      { immediate: true },
    );

    // Emit changes when operator or value changes
    const emitChange = () => {
      let values: unknown;

      // For dateRange, the filterValue is already a DateRangeFilterValue object
      if (filterType.value === 'dateRange') {
        values = filterValue.value;
        // Don't emit if the dateRange is empty (interval === 'empty')
        if (isDateRangeFilterValue(values) && values.interval === 'empty') {
          emit('update:modelValue', null);
          return;
        }
      } else if (isRangeOperatorSelected.value) {
        values = [rangeStart.value, rangeEnd.value];
      } else {
        values = filterValue.value;
      }

      const filter: SpmActiveFilter = {
        field: props.fieldConfig.name,
        operator: selectedOperator.value,
        values,
        relationFilter: props.fieldConfig.relationFilterConfig,
      };

      emit('update:modelValue', filter);
    };

    // Watch for changes
    watch(selectedOperator, emitChange);
    watch(filterValue, emitChange);
    watch(rangeStart, emitChange);
    watch(rangeEnd, emitChange);

    return {
      t,
      filterType,
      showOperatorSelector,
      operatorOptions,
      selectedOperator,
      filterValue,
      rangeStart,
      rangeEnd,
      isRangeOperatorSelected,
      resolvedOptions,
    };
  },
});
</script>

<style lang="scss" scoped>
.spm-filter-card {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 6px;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.75rem;
    background: #f5f5f5;
  }

  &__field-info {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  &__field-name {
    font-size: 0.75rem;
    font-weight: 500;
    color: #666;
  }

  &__remove {
    width: 1.5rem !important;
    height: 1.5rem !important;
    padding: 0 !important;
    color: #999 !important;
    background: transparent !important;
    border: none !important;

    &:hover {
      color: #e53935 !important;
      background: transparent !important;
    }
  }

  &__body {
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    background: #fff;
  }

  &__operator {
    width: 100%;
  }

  &__input {
    width: 100%;
  }

  &__range {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__range-input {
    flex: 1;
  }

  &__range-separator {
    color: #999;
    font-size: 0.75rem;
  }

  // PrimeVue overrides
  :deep(.p-dropdown),
  :deep(.p-multiselect),
  :deep(.p-inputtext),
  :deep(.p-inputnumber) {
    width: 100%;
    border-radius: 4px;
    font-size: 0.8125rem;
  }

  :deep(.p-inputnumber-input) {
    width: 100%;
    border-radius: 4px;
  }

  :deep(.p-dropdown:not(.p-disabled):hover),
  :deep(.p-multiselect:not(.p-disabled):hover),
  :deep(.p-inputtext:not(.p-disabled):hover) {
    border-color: #94c840;
  }

  :deep(.p-dropdown:not(.p-disabled).p-focus),
  :deep(.p-multiselect:not(.p-disabled).p-focus),
  :deep(.p-inputtext:not(.p-disabled):focus) {
    border-color: #94c840;
    box-shadow: 0 0 0 1px rgba(148, 200, 64, 0.25);
  }
}
</style>
